/**
 * Which panel this DEVICE opens after login (gate-panel plan, decision 5) — global client state, so
 * Zustand (R-18.106), persisted like the theme preference: a NON-SENSITIVE UI preference, never a
 * credential (R-18.198/R-18.199). `null` = no choice made yet, so a user who can use both panels gets
 * the choice screen once and the device remembers the answer.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/** The two panels the SPA ships: the main admin shell and the mobile gate (intake/handover) panel. */
export type PanelChoice = 'main' | 'gate';

interface PanelState {
  panel: PanelChoice | null;
  setPanel: (panel: PanelChoice) => void;
}

export const usePanelStore = create<PanelState>()(
  persist(
    (set) => ({
      panel: null,
      setPanel: (panel) => set({ panel }),
    }),
    {
      name: 'stuttgart-crm.panel',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ panel: s.panel }),
    },
  ),
);

export interface PanelTargetInput {
  /** Holds at least one capability from the main shell's nav tree (`canUseMainPanel`). */
  canUseMain: boolean;
  /** Holds `gate.access`. */
  canUseGate: boolean;
  /** The device's remembered choice, null when never made. */
  storedPanel: PanelChoice | null;
}

/**
 * The post-login landing for a user (gate-panel plan, decision 5): one usable panel → straight into
 * it; both → the remembered choice, or the choice screen when the device has none. A user with
 * neither lands on the main shell — its ungated pages (dashboard, instructions) still apply.
 */
export function resolvePanelTarget({
  canUseMain,
  canUseGate,
  storedPanel,
}: PanelTargetInput): '/' | '/gate' | '/choose-panel' {
  if (!canUseGate) return '/';
  if (!canUseMain) return '/gate';
  if (storedPanel === 'gate') return '/gate';
  if (storedPanel === 'main') return '/';
  return '/choose-panel';
}
